/* PawTronic interaction measurement. No cart interception, PII, cookies or order events. */
(() => {
  'use strict';
  if (window.__knownPawtronicTracking) return;
  const configNode = document.getElementById('known-pawtronic-tracking-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  if (!['index', 'product'].includes(config.pageType) || config.measurementId !== 'G-RWW1THGLNV') return;
  window.__knownPawtronicTracking = true;
  const query = new URLSearchParams(location.search);
  const preview = config.preview !== false || Boolean(window.Shopify?.designMode) || query.has('preview_theme_id');
  const debug = preview && query.get('pt_tracking_debug') === '1';
  const pageType = config.pageType === 'index' ? 'homepage' : 'product';
  const sections = new Set(['hero-banner', 'value-props', 'video-scroll', 'neighborhood-map', 'how-it-works',
    'reviews', 'feature-grid', 'video', 'compare-solution', 'shop-pawtronic', 'multi-dog-packages',
    'faq', 'promo-banner', 'testimonials', 'main-product', 'known-pawtronic-specs', 'image-video-frame', 'compare']);
  const eventNames = new Set(['pt_section_view', 'pt_cta_click', 'pt_package_select', 'pt_color_select',
    'pt_add_to_cart_click', 'pt_checkout_click', 'pt_cart_open', 'pt_faq_open', 'pt_reviews_toggle',
    'pt_carousel_interaction', 'pt_support_click', 'pt_video_open', 'pt_video_start', 'pt_video_progress', 'pt_product_detail_open']);
  const parameterNames = new Set(['section_name', 'action', 'target_path', 'package_count',
    'item_id', 'item_variant', 'collar_slot', 'faq_id', 'faq_group', 'faq_question', 'video_percent', 'detail_title']);
  const history = [];
  let debugOutput;
  const consentAllowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  function drawDebug() {
    if (!debugOutput) return;
    debugOutput.textContent = 'Draft preview: no new tracking events sent to Google.\nAnalytics consent: ' +
      (consentAllowed() ? 'allowed' : 'not allowed / unavailable') + '\n' +
      history.map(row => JSON.stringify(row)).join('\n');
  }
  function emit(name, parameters = {}) {
    if (!eventNames.has(name)) return;
    const clean = { page_type: pageType, tracking_version: config.version };
    for (const [key, value] of Object.entries(parameters)) {
      if (!parameterNames.has(key)) continue;
      if (typeof value === 'number' && Number.isFinite(value)) clean[key] = value;
      else if (typeof value === 'string' && !/[@\u0000-\u001f]/.test(value)) {
        clean[key] = value.replace(/\s+/g, ' ').trim().slice(0, 100);
      }
    }
    if (preview) {
      if (debug) { history.push({ event: name, ...clean }); if (history.length > 40) history.shift(); drawDebug(); }
      return;
    }
    if (!consentAllowed()) return; // No replay of pre-consent interactions.
    // Use the existing Google tag destination; do not load/configure another tag.
    window.dataLayer = window.dataLayer || [];
    function command() { window.dataLayer.push(arguments); }
    command('event', name, { ...clean, send_to: config.measurementId });
  }
  function sectionName(node) {
    const section = node?.closest?.('[id][data-section-id], #multi-dog-packages, #known-pawtronic-specs');
    if (sections.has(section?.id)) return section.id;
    if (node?.closest?.('[data-review-preview]')) return 'reviews';
    if (node?.closest?.('[data-variant-gallery]')) return 'main-product';
    if (node?.closest?.('header, [role="banner"]')) return 'header';
    if (node?.closest?.('footer')) return 'footer';
    return 'other';
  }
  function productId(node) {
    try {
      const root = node.closest('[x-data="product"]');
      const data = JSON.parse(root?.querySelector('[data-product-info]')?.textContent || '{}');
      return String(data.product?.id || '');
    } catch (_) { return ''; }
  }
  function afterUpdate(callback) { window.requestAnimationFrame(() => window.requestAnimationFrame(callback)); }
  function faqGroup(button) {
    let sibling = button.closest('article')?.previousElementSibling;
    while (sibling && !sibling.matches('.known-faq-group-heading')) sibling = sibling.previousElementSibling;
    return sibling?.textContent.includes('Understanding') ? 'category' : 'product';
  }
  const videoIntent = new WeakMap();
  function handleClick(event) {
    const node = event.target?.closest?.('a,button');
    if (!node || node.disabled || node.closest('#known-tracking-debug')) return;
    const section = sectionName(node);
    const common = { section_name: section };
    const clickCode = node.getAttribute('@click') || node.getAttribute('@click.stop') || node.getAttribute('@click.prevent') || '';
    if (node.matches('.known-review-preview__button')) {
      afterUpdate(() => emit('pt_reviews_toggle', { ...common,
        action: node.getAttribute('aria-expanded') === 'true' ? 'expand' : 'collapse' }));
    } else if (node.closest('#faq') && node.hasAttribute('aria-expanded') && node.hasAttribute('aria-controls')) {
      afterUpdate(() => {
        if (node.getAttribute('aria-expanded') === 'true') emit('pt_faq_open', { ...common,
          faq_id: node.getAttribute('aria-controls'), faq_group: faqGroup(node), faq_question: node.textContent.trim() });
      });
    } else if (node.closest('#main-product') && /active ===/.test(clickCode)) {
      const content = node.nextElementSibling;
      afterUpdate(() => { if (content && !content.hasAttribute('x-cloak') && content.style.display !== 'none') {
        emit('pt_product_detail_open', { ...common, detail_title: node.getAttribute('aria-label') || '' });
      } });
    } else if (node.getAttribute('aria-label') === 'Open video') {
      emit('pt_video_open', common);
    } else if (node.matches('.bundle-picker__option')) {
      if (node.getAttribute('aria-pressed') === 'true') return;
      const count = Number(clickCode.match(/setBundleCount\((\d)\)/)?.[1]);
      afterUpdate(() => { if (node.getAttribute('aria-pressed') === 'true' && count >= 1 && count <= 4) {
        emit('pt_package_select', { ...common, package_count: count, item_id: productId(node) });
      } });
    } else if (node.matches('.featured-product__swatch')) {
      if (node.classList.contains('is-active')) return;
      afterUpdate(() => { if (node.classList.contains('is-active')) emit('pt_color_select', {
        ...common, item_id: productId(node), item_variant: node.getAttribute('aria-label') || '', collar_slot: 1
      }); });
    } else if (clickCode.includes('setSlotColor(')) {
      if (node.getAttribute('aria-pressed') === 'true') return;
      afterUpdate(() => { if (node.getAttribute('aria-pressed') === 'true') emit('pt_color_select', {
        ...common, item_id: productId(node), item_variant: node.getAttribute('aria-label') || '',
        collar_slot: Number(node.parentElement?.parentElement?.textContent.match(/Collar\s*(\d)/)?.[1]) || 1
      }); });
    } else if (node.hasAttribute('data-atc-btn') || clickCode.includes('addToCart(')) {
      // Intent is not a successful add: Shopify's existing pixel owns add_to_cart.
      emit('pt_add_to_cart_click', { ...common, item_id: productId(node) });
    } else if (node.getAttribute('name') === 'checkout' || /(?:\/checkout\b|checkout\()/.test(node.getAttribute('href') || clickCode)) {
      emit('pt_checkout_click', common);
    } else if (node.getAttribute('aria-label') === 'Open cart') {
      emit('pt_cart_open', common);
    } else if (/slider\.go|thumbSlider\.go/.test(clickCode) || node.matches('.splide__arrow, .splide__pagination__page')) {
      emit('pt_carousel_interaction', { ...common, action: 'button' });
    } else if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (/^(mailto|tel):/i.test(href)) emit('pt_support_click', { ...common, action: href.startsWith('tel:') ? 'phone' : 'email' });
      else {
        try {
          const target = new URL(href, location.href);
          if (target.origin === location.origin && (/^\/products\//.test(target.pathname) ||
            ['/pages/how-it-works', '/pages/reviews', '/pages/contact'].includes(target.pathname) ||
            (target.hash && target.pathname === location.pathname))) {
            const packageCount = Number(target.searchParams.get('collars'));
            emit('pt_cta_click', { ...common, target_path: target.pathname + target.hash,
              ...(packageCount >= 1 && packageCount <= 4 ? { package_count: packageCount } : {}) });
          }
        } catch (_) { /* Malformed links do not interrupt navigation. */ }
      }
    }
  }
  document.addEventListener('click', event => {
    // Include clicks on native video controls, but never count autoplay as interest.
    const videoRoot = event.target?.closest?.('[x-data*="videoToggler"]');
    if (videoRoot) {
      videoRoot.querySelectorAll('video').forEach(video => videoIntent.set(video, Date.now()));
    }
    try { handleClick(event); } catch (_) { /* Measurement must never break shopping. */ }
  }, true);
  const videoStarted = new WeakSet();
  const videoProgress = new WeakMap();
  document.addEventListener('play', event => {
    const video = event.target;
    if (video.tagName !== 'VIDEO' || Date.now() - (videoIntent.get(video) || 0) > 30000) return;
    if (!videoStarted.has(video)) { videoStarted.add(video); emit('pt_video_start', { section_name: sectionName(video) }); }
  }, true);
  document.addEventListener('timeupdate', event => {
    const video = event.target;
    if (video.tagName !== 'VIDEO' || !videoStarted.has(video) || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const seen = videoProgress.get(video) || new Set();
    for (const percent of [25, 50, 75, 100]) {
      if (video.currentTime / video.duration >= (percent === 100 ? 0.99 : percent / 100) && !seen.has(percent)) {
        seen.add(percent); emit('pt_video_progress', { section_name: sectionName(video), video_percent: percent });
      }
    }
    videoProgress.set(video, seen);
  }, true);
  let swipe;
  document.addEventListener('pointerdown', event => {
    const slider = event.target?.closest?.('.splide');
    swipe = slider ? { slider, x: event.clientX, id: event.pointerId } : null;
  }, { passive: true });
  document.addEventListener('pointerup', event => {
    if (swipe && swipe.id === event.pointerId && Math.abs(event.clientX - swipe.x) >= 50) {
      emit('pt_carousel_interaction', { section_name: sectionName(swipe.slider), action: 'swipe' });
    }
    swipe = null;
  }, { passive: true });
  document.addEventListener('pointercancel', () => { swipe = null; }, { passive: true });
  if (typeof IntersectionObserver !== 'undefined') {
    const pending = new Map();
    const seen = new Set();
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const name = entry.target.id;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) {
          clearTimeout(pending.get(name)); pending.delete(name); continue;
        }
        if (seen.has(name) || pending.has(name)) continue;
        pending.set(name, setTimeout(() => {
          pending.delete(name);
          if (document.visibilityState !== 'visible') return;
          // Allow a section to qualify again after consent rather than storing a pre-consent event.
          if (!preview && !consentAllowed()) return;
          seen.add(name); emit('pt_section_view', { section_name: name }); observer.unobserve(entry.target);
        }, 1000));
      }
    }, { threshold: [0, 0.25] });
    for (const name of sections) {
      const node = document.getElementById(name);
      if (node) observer.observe(node);
    }
    document.addEventListener('visitorConsentCollected', () => {
      drawDebug();
      for (const name of sections) {
        const node = document.getElementById(name);
        if (node && !seen.has(name)) { observer.unobserve(node); observer.observe(node); }
      }
    });
  }
  if (debug) {
    const panel = document.createElement('details');
    panel.id = 'known-tracking-debug';
    panel.style.cssText = 'position:fixed;bottom:64px;right:12px;z-index:10000;max-width:min(520px,94vw);background:#fff;color:#111;border:2px solid #984a3b;border-radius:12px;padding:12px;font:13px/1.5 system-ui;box-shadow:0 4px 24px #0002';
    const summary = document.createElement('summary');
    summary.textContent = 'Tracking preview — nothing sent to Google';
    summary.style.cursor = 'pointer';
    debugOutput = document.createElement('pre');
    debugOutput.style.cssText = 'white-space:pre-wrap;overflow:auto;max-height:35vh;font:11px/1.5 monospace';
    panel.append(summary, debugOutput); document.body.append(panel); drawDebug();
  }
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => drawDebug()); }
    catch (_) { /* Missing privacy API means no events sent, not implied consent. */ }
  }
})();
